import React, { useState, useRef, useEffect } from 'react';
import { Refund } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';
import { toast } from 'react-toastify';

type RefundsTableProps = {
  refunds: Refund[];
  onEdit: (refund: Refund) => void;
  onSuccess: () => void;
};

const RefundsTable: React.FC<RefundsTableProps> = ({ refunds, onEdit, onSuccess }) => {
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const buttonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openActionMenuId !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('.action-menu')) {
          setOpenActionMenuId(null);
          setMenuPosition(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionMenuId]);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="table-fixed w-full bg-white border border-gray-200 rounded-md shadow-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-sm font-semibold text-gray-700">
              <th className="hidden md:table-cell px-4 py-2">Created At</th>
              <th className="hidden md:table-cell px-4 py-2">Order Reference</th>
              <th className="hidden md:table-cell px-4 py-2">Customer</th>
              <th className="px-4 py-2">Description</th>
              <th className="px-4 py-2">Amount</th>
              <th className="hidden md:table-cell px-4 py-2">Refunded At</th>
              <th className="px-4 py-2">Processed</th>
              <th className="hidden md:table-cell px-4 py-2">Type</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
        <tbody>
          {refunds.map((refund) => (
            <tr
              key={refund.id}
              className={`border-t border-gray-100 text-sm text-gray-800 relative overflow-visible hover:bg-yellow-100 ${
                openActionMenuId === refund.id ? 'bg-yellow-100' : ''
              }`}
            >
              <td className="hidden md:table-cell px-4 py-2">
                {new Date(refund.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  })}
              </td>
              <td className="hidden md:table-cell px-4 py-2">{refund.order.order_reference}</td>
              <td className="hidden md:table-cell px-4 py-2">{refund.customer.identifier}</td>
              <td className="px-4 py-2">{refund.description}</td>
              <td className="px-4 py-2">{formatCurrency(refund.amount)}</td>
              <td className="hidden md:table-cell px-4 py-2">
                {refund.refunded_at ?
                  new Date(refund.refunded_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  })
                  : "N/A"}
              </td>
              <td className="px-4 py-2">
                {refund.refunded_at ? (
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full" />
                ) : (
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full" />
                )}
              </td>
              <td className="hidden md:table-cell px-4 py-2">
                <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                  refund.refund_type === 'credit'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {refund.refund_type === 'credit' ? 'Credit' : 'Cash'}
                </span>
              </td>
              <td className="px-4 py-2 relative overflow-visible">
                <div className="relative inline-block text-left action-menu">
                  <button
                    ref={(el) => { buttonRefs.current[refund.id] = el; }}
                    onClick={(e) => {
                      if (refund.refund_type === 'credit') return;
                      e.stopPropagation();
                      const newMenuId = openActionMenuId === refund.id ? null : refund.id;
                      setOpenActionMenuId(newMenuId);

                      if (newMenuId && buttonRefs.current[refund.id]) {
                        const rect = buttonRefs.current[refund.id]!.getBoundingClientRect();
                        const menuHeight = 150; // Approximate height of menu (fewer items than extra income)
                        const spaceBelow = window.innerHeight - rect.bottom;
                        const shouldShowAbove = spaceBelow < menuHeight;

                        setMenuPosition({
                          top: shouldShowAbove ? rect.top - menuHeight : rect.bottom + 8,
                          right: window.innerWidth - rect.right
                        });
                      }
                    }}
                    className={`p-1 rounded-full focus:outline-none ${refund.refund_type === 'credit' ? 'opacity-25 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                  >
                    <span className="text-xl">⋯</span>
                  </button>
                  {openActionMenuId === refund.id && menuPosition && (
                    <div className="fixed w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                         style={{
                           top: `${menuPosition.top}px`,
                           right: `${menuPosition.right}px`
                         }}>
                      <div className="py-1">
                        {refund.refund_type !== 'credit' && (
                          <button
                            onClick={() => {
                              onEdit(refund);
                              setOpenActionMenuId(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Edit
                          </button>
                        )}
                        {refund.refund_type !== 'credit' && (
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this refund?')) {
                                try {
                                  await fetchWithAuth(`/refunds/${refund.id}`, {
                                    method: 'DELETE',
                                  });
                                  onSuccess();
                                  toast.success('Refund deleted successfully!');
                                } catch (error) {
                                  toast.error('Failed to delete refund.');
                                } finally {
                                  setOpenActionMenuId(null);
                                }
                              }
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default RefundsTable;
